import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { AuthContext } from './context.ts'
import {
  buildAuthorizeUrl,
  createCodeChallenge,
  randomUrlSafeString,
  returnToFrom,
  savePendingAuthorization,
  takePendingAuthorization,
} from './pkce.ts'
import { clearSignedInHint, readSignedInHint, writeSignedInHint } from './hint.ts'
import type { AuthConfig, AuthContextValue, AuthState } from './types.ts'

// A first-party SPA's OAuth session, owned by the shell rather than by any
// screen.
//
// Auth is cross-cutting: some screens read anonymously and some do not, and
// every authenticated screen needs the same token, the same refresh, and the
// same request headers. Putting it here means a screen consumes `useAuth()`
// instead of re-implementing a flow.
//
// It lives in a package rather than in one of the SPAs because they run the
// *same* flow — same Hydra client, same origin — and this is the code where a
// fork costs the most: every token-handling fix would have to be discovered and
// applied twice, and drift between copies stays silent until one of them breaks
// or leaks. What legitimately differs per app is passed in: the endpoint that
// resolves the caller, and the path its registered redirect URI points at.
//
// WHERE THE TOKENS LIVE, and why the split:
//
//   • The refresh token never touches this file. The host service exchanges the
//     code behind `config.sessionUrl` and keeps the refresh token in an httpOnly
//     cookie, so no script on this origin can read it. That is what makes a
//     session survive a reload *and* a browser close: the page comes back with
//     nothing, asks the server to resume, and the cookie does the rest.
//   • The access token is held in memory only, exactly as before. It is
//     short-lived, it dies with the page, and it has to be reachable from
//     JavaScript because the portal spends it on a *cross-origin* call — the docs
//     playground's prefill reads curri-api's GraphQL endpoint, which no
//     same-origin cookie could ever reach.
//
// An XSS on this origin can still take the access token and use it until it
// expires. It cannot take the session, and it cannot keep anything once the tab
// closes. That is the trade, and it is why the refresh token is worth the extra
// round trip that in-storage tokens would have avoided.

// Dated API version this build speaks. The partner REST surface requires the
// header on every request (INT-327), so the fetch wrapper — not each caller —
// sends it. Surfaces that do not gate on it ignore it, and these are same-origin
// requests, so the extra header costs no preflight.
export const CURRI_VERSION = '2026-07-15'

// `expires_in` is deliberately unused: refresh is reactive, not scheduled —
// `apiFetch` retries once on a 401. Reading the field would send the next reader
// looking for a timer that does not exist.
interface SessionResponse {
  access_token?: string
}

export interface AuthProviderProps<TConfig extends AuthConfig> {
  /** The environment's configuration document — see `loadAuthConfig`. */
  config: TConfig
  /**
   * Same-origin path that resolves the caller for this app: `/api/me` for the
   * developer portal, `/centcom/api/session` for CENTCOM. It must answer the
   * service's `{ data }` envelope, and whatever sits under `data` becomes the
   * authenticated state's `caller`.
   */
  callerPath: string
  /**
   * Path half of this app's OAuth redirect URI, resolved against
   * `config.publicUrl`. The portal is served from the origin root and redirects
   * to `/`; an SPA served under a prefix redirects to that prefix (`/centcom/`).
   * Whatever is used here has to be registered on the Hydra client.
   */
  redirectPath: string
  children: ReactNode
}

export const AuthProvider = <TConfig extends AuthConfig>({
  config,
  callerPath,
  redirectPath,
  children,
}: AuthProviderProps<TConfig>) => {
  // Starts at `authenticating` when the last visit left a signed-in hint, so a
  // returning visitor sees the signed-in shell rather than a flash of the
  // anonymous one while the resume below is in flight. The hint is not a
  // credential and proves nothing — the resume decides, and clears it on a 401.
  const [state, setState] = useState<AuthState<unknown>>(() =>
    readSignedInHint() ? { status: 'authenticating' } : { status: 'anonymous' }
  )
  const [restoredPath, setRestoredPath] = useState<string | null>(null)
  // A ref, not state: a token change must not re-render the tree, and apiFetch
  // has to read the freshest value without being re-created on every refresh.
  const accessToken = useRef<string | null>(null)

  const redirectUri = `${config.publicUrl}${redirectPath}`
  const refreshUrl = `${config.sessionUrl}/refresh`

  // Every call to the session surface is same-origin and cookie-bearing. The
  // cookie is `SameSite=Lax` and path-scoped to the session routes, so it rides
  // along on these and on nothing else the app does.
  const postSession = useCallback(
    async (url: string, body?: Record<string, string>): Promise<boolean> => {
      // Every failure — offline, DNS, a 500, a body with no token — collapses to
      // `false`, because every caller wants the same thing from all of them:
      // treat this page as signed out and offer sign-in. Letting a rejection
      // escape would be worse than useless here: the resume below runs on every
      // single page load, so an unhandled rejection would strand the shell in
      // `authenticating` with a spinner and no way out.
      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          ...(body
            ? {
                headers: { 'content-type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(body),
              }
            : {}),
        })
        if (!response.ok) return false

        const session = (await response.json()) as SessionResponse
        if (!session.access_token) return false

        accessToken.current = session.access_token
        return true
      } catch {
        return false
      }
    },
    []
  )

  // A same-origin relative path, not one built from `config.publicUrl`: the API
  // is what serves this bundle, so relative is always right and keeps working on
  // any origin the service answers on (a port-forward, a preview container, a
  // staging alias) without CORS. `publicUrl` is reserved for the OAuth redirect
  // URI, which has to be the canonical registered value.
  const loadCaller = useCallback(async (): Promise<void> => {
    const response = await fetch(callerPath, {
      headers: {
        Authorization: `Bearer ${accessToken.current ?? ''}`,
        'Curri-Version': CURRI_VERSION,
      },
    })

    if (!response.ok) {
      clearSignedInHint()
      setState({
        status: 'error',
        message:
          response.status === 503
            ? 'Sign-in is unavailable right now — the identity service could not be reached. This is not a problem with your account.'
            : 'That session could not be verified. Try signing in again.',
      })
      return
    }

    const body = (await response.json()) as { data: unknown }
    writeSignedInHint()
    setState({ status: 'authenticated', caller: body.data })
    // `callerPath` is the only dependency: the token is read from a ref, so
    // nothing else in here comes from props.
  }, [callerPath])

  const signIn = useCallback(() => {
    if (!config.clientId) {
      // No client provisioned for this environment. Say so plainly rather than
      // redirecting to an authorization server with `client_id=`, which renders
      // an opaque error page on their side.
      setState({
        status: 'error',
        message:
          'Sign-in is not configured for this environment yet (no OAuth client is provisioned).',
      })
      return
    }

    setState({ status: 'authenticating' })
    void (async () => {
      const verifier = randomUrlSafeString()
      // `oauthState`, not `state`: the component's auth state is also in scope
      // here, and one of the two being a CSRF nonce is worth naming.
      const oauthState = randomUrlSafeString(16)
      savePendingAuthorization({
        verifier,
        state: oauthState,
        returnTo: returnToFrom(window.location),
      })
      window.location.assign(
        buildAuthorizeUrl({
          authorizeUrl: config.authorizeUrl,
          clientId: config.clientId,
          redirectUri,
          scopes: config.scopes,
          state: oauthState,
          codeChallenge: await createCodeChallenge(verifier),
          audience: config.resourceUri,
        })
      )
    })()
  }, [
    config.authorizeUrl,
    config.clientId,
    config.scopes,
    config.resourceUri,
    redirectUri,
  ])

  const signOut = useCallback(() => {
    // Local state goes first and unconditionally. The visitor asked to be signed
    // out; a slow or failing revoke must not leave them looking signed in.
    accessToken.current = null
    clearSignedInHint()
    setState({ status: 'anonymous' })
    void fetch(config.sessionUrl, {
      method: 'DELETE',
      credentials: 'same-origin',
    }).catch(() => {
      // The cookie is `httpOnly`, so the page cannot clear it itself and the
      // server is the only one who can. If this never lands the cookie outlives
      // the click and the next load resumes the session — which is worth no
      // handling here beyond not crashing: the next sign-out attempt retries it,
      // and the token still expires on its own.
    })
  }, [config.sessionUrl])

  // One mount effect for both entry paths, because they are the same decision:
  // "how does this page become signed in?" Either a redirect just came back with
  // a code to spend, or there is no code and the only hope is an existing
  // session cookie. Splitting them into two effects raced — both would run on a
  // redirect load, and the resume could beat the exchange with a 401 that
  // cleared state the exchange was about to fill.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const returnedState = params.get('state')
    const authError = params.get('error')

    // No redirect in progress: this is an ordinary page load, so try to resume
    // whatever session the cookie still carries. THIS is what makes a reload —
    // and a browser restart — keep the visitor signed in.
    if (!code && !authError) {
      void (async () => {
        const resumed = await postSession(refreshUrl)
        if (!resumed) {
          // Anonymous, silently. No cookie, or one the authorization server
          // would not honour — both are the ordinary state for a visitor who has
          // not signed in, and neither is an error worth showing.
          clearSignedInHint()
          setState({ status: 'anonymous' })
          return
        }
        await loadCaller()
      })()
      return
    }

    const pending = takePendingAuthorization()
    // Strip the code out of the URL before anything else can log or share it.
    // Always to the redirect path — restoring where the visitor came from is the
    // shell's job, below, because it owns `pathname` and has to re-render with it.
    window.history.replaceState({}, '', redirectPath)
    if (pending?.returnTo && pending.returnTo !== redirectPath) {
      setRestoredPath(pending.returnTo)
    }

    if (authError) {
      setState({
        status: 'error',
        message: `Sign-in was not completed (${authError}).`,
      })
      return
    }
    if (!pending || pending.state !== returnedState) {
      // A mismatched or missing state is the CSRF signal the parameter exists
      // for; there is no safe way to continue.
      setState({
        status: 'error',
        message: 'Sign-in could not be verified. Please try again.',
      })
      return
    }

    setState({ status: 'authenticating' })
    void (async () => {
      const exchanged = await postSession(config.sessionUrl, {
        code: code as string,
        redirect_uri: redirectUri,
        code_verifier: pending.verifier,
      })
      if (!exchanged) {
        setState({
          status: 'error',
          message: 'The authorization code could not be exchanged for a token.',
        })
        return
      }
      await loadCaller()
    })()
  }, [
    postSession,
    loadCaller,
    redirectPath,
    redirectUri,
    refreshUrl,
    config.sessionUrl,
  ])

  const apiFetch = useCallback(
    async (path: string, init: RequestInit = {}): Promise<Response> => {
      const send = () => {
        // Through `Headers` rather than a spread: `HeadersInit` is also a
        // `Headers` instance or an array of entries, and spreading either of
        // those silently drops every header the caller passed. This is the
        // shared entry point for every screen, so it has to take all three
        // shapes. Ours are set last, so they still win.
        const headers = new Headers(init.headers)
        headers.set('Curri-Version', CURRI_VERSION)
        if (accessToken.current) {
          headers.set('Authorization', `Bearer ${accessToken.current}`)
        }
        return fetch(path, { ...init, headers })
      }

      const response = await send()
      // One silent refresh attempt on 401. Anything else — including a 503 from
      // an identity outage — is handed back untouched so the caller can tell an
      // expired session apart from an unavailable one.
      if (response.status !== 401) return response

      const refreshed = await postSession(refreshUrl)
      if (!refreshed) {
        signOut()
        return response
      }
      return send()
    },
    [postSession, refreshUrl, signOut]
  )

  const getAccessToken = useCallback(() => accessToken.current, [])

  const value = useMemo<AuthContextValue<unknown, TConfig>>(
    () => ({
      state,
      config,
      signIn,
      signOut,
      apiFetch,
      getAccessToken,
      restoredPath,
    }),
    [state, config, signIn, signOut, apiFetch, getAccessToken, restoredPath]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
